import { TextKey } from '@mews-commander/enums';
import { LocalizedCurrencyValue, LocalizedEnum, LocalizedText } from '@mews/framework';
import { Dash, DividerList, EnterpriseLink, LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { EmptyState, Stack, Tag, Typography } from '@optimus-web/core';
import { PaymentMethodType } from '@mews-commander/data-access';
import { Target } from '@mews/enums';
import { ModalSidePanel } from '../../../../components';
import { useGetPayoutTransactionsDetail } from './queries';
import { PaymentDetailsSidePanelRow } from './PaymentDetailsSidePanelRow';
import { PaymentDetailsSidePanelGrid } from './PaymentDetailsSidePanelGrid';
import { PaymentDetailsSidePanelReservations } from './PaymentDetailsSidePanelReservations';
import { hasLinkedPayment, paymentSettlementStateTagVariantMap } from './utils';
import { PaymentDetailSidePanelPaymentMethod } from './PaymentDetailsSidePanelPaymentMethod';
import { PaymentDetailSidePanelBill } from './PaymentDetailsSidePanelBill';
import { PaymentDetailsSidePanelTransactionHistory } from './PaymentDetailsSidePanelTransactionHistory';

export const PaymentDetailSidePanel = ({
    isSidePanelOpen,
    closeSidePanel,
    selectedPaymentId,
}: {
    isSidePanelOpen: boolean;
    closeSidePanel: () => void;
    selectedPaymentId: string;
}) => {
    const {
        data: paymentDetail,
        isFetching,
        isError,
    } = useGetPayoutTransactionsDetail({
        transactionId: selectedPaymentId,
    });

    return (
        <ModalSidePanel
            title={<LocalizedText textKey={TextKey.TransactionDetails} />}
            onClose={closeSidePanel}
            width="small"
            headerActions={null}
            loading={isFetching}
            opened={isSidePanelOpen}
            content={
                paymentDetail && !isError ? (
                    <Stack vertical spacing="400">
                        <DividerList spacing="300">
                            <Stack vertical>
                                <Typography textStyle="bodyLargeStrong" variant="tertiary" as="div">
                                    <LocalizedText textKey={TextKey.PayoutNetAmount} />
                                </Typography>
                                <Typography textStyle="highlightMedium" as="div">
                                    <LocalizedCurrencyValue
                                        value={{
                                            value: paymentDetail.transaction.net.value,
                                            currency: paymentDetail.transaction.net.currencyCode,
                                        }}
                                    />
                                </Typography>
                            </Stack>

                            <PaymentDetailsSidePanelGrid>
                                <PaymentDetailsSidePanelRow labelTextKey={TextKey.Status}>
                                    <Tag
                                        variant={paymentSettlementStateTagVariantMap[paymentDetail.transaction.status]}
                                    >
                                        <LocalizedEnum
                                            prefix="PaymentSettlementState"
                                            value={paymentDetail.transaction.status}
                                        />
                                    </Tag>
                                </PaymentDetailsSidePanelRow>
                                <PaymentDetailsSidePanelRow labelTextKey={TextKey.Type}>
                                    <Typography textStyle="bodyMedium" as="div">
                                        <LocalizedEnum
                                            prefix="PayoutDetailResultType"
                                            value={paymentDetail.transaction.type}
                                        />
                                    </Typography>
                                </PaymentDetailsSidePanelRow>
                                <PaymentDetailsSidePanelRow labelTextKey={TextKey.Created}>
                                    <Typography textStyle="bodyMedium" as="div">
                                        <LocalizedDateTimeFromUtc utcString={paymentDetail.transaction.createdUtc} />
                                    </Typography>
                                </PaymentDetailsSidePanelRow>
                                <PaymentDetailsSidePanelRow labelTextKey={TextKey.Settled}>
                                    {paymentDetail.transaction.settledUtc ? (
                                        <Typography textStyle="bodyMedium" as="div">
                                            <LocalizedDateTimeFromUtc
                                                utcString={paymentDetail.transaction.settledUtc}
                                            />
                                        </Typography>
                                    ) : (
                                        <Dash />
                                    )}
                                </PaymentDetailsSidePanelRow>
                                <PaymentDetailsSidePanelRow labelTextKey={TextKey.Identifier}>
                                    <Typography textStyle="bodyMedium" as="div">
                                        {paymentDetail.transaction.id}
                                    </Typography>
                                </PaymentDetailsSidePanelRow>
                            </PaymentDetailsSidePanelGrid>

                            {paymentDetail.paymentMethod.value && (
                                <PaymentDetailsSidePanelGrid>
                                    <PaymentDetailSidePanelPaymentMethod paymentMethod={paymentDetail.paymentMethod} />
                                </PaymentDetailsSidePanelGrid>
                            )}

                            {hasLinkedPayment[paymentDetail.transaction.type] && (
                                <PaymentDetailsSidePanelGrid>
                                    {(paymentDetail.paymentMethod.discriminator === PaymentMethodType.SepaDirectDebit ||
                                        paymentDetail.paymentMethod.discriminator === PaymentMethodType.PaymentCard) &&
                                        paymentDetail.paymentMethod.value.paymentPlan && (
                                            <PaymentDetailsSidePanelRow labelTextKey={TextKey.Customer}>
                                                <Typography textStyle="bodyMedium" as="div">
                                                    <EnterpriseLink
                                                        to="Customer/Detail/$id"
                                                        params={{
                                                            id: paymentDetail.paymentMethod.value.paymentPlan
                                                                .customerId,
                                                        }}
                                                        target={Target.Blank}
                                                        data-test-id="payout-payment-detail-customer"
                                                        inherit
                                                    >
                                                        {paymentDetail.paymentMethod.value.paymentPlan.customerName || (
                                                            <Dash />
                                                        )}
                                                    </EnterpriseLink>
                                                </Typography>
                                            </PaymentDetailsSidePanelRow>
                                        )}
                                    <PaymentDetailSidePanelBill bill={paymentDetail.bill} />
                                    <PaymentDetailsSidePanelRow labelTextKey={TextKey.Reservation}>
                                        <PaymentDetailsSidePanelReservations
                                            reservations={paymentDetail.reservations}
                                        />
                                    </PaymentDetailsSidePanelRow>
                                    {(paymentDetail.paymentMethod.discriminator === PaymentMethodType.SepaDirectDebit ||
                                        paymentDetail.paymentMethod.discriminator === PaymentMethodType.PaymentCard) &&
                                        paymentDetail.paymentMethod.value.paymentPlan && (
                                            <PaymentDetailsSidePanelRow labelTextKey={TextKey.PaymentPlan}>
                                                <Typography textStyle="bodyMedium" as="div">
                                                    <EnterpriseLink
                                                        to="Payment/Index/$accountId"
                                                        params={{
                                                            accountId:
                                                                paymentDetail.paymentMethod.value.paymentPlan
                                                                    .customerId,
                                                        }}
                                                        search={{ activeSegment: 'Plans' }}
                                                        target={Target.Blank}
                                                        data-test-id="payout-payment-detail-payment-plan"
                                                        inherit
                                                    >
                                                        {paymentDetail.paymentMethod.value.paymentPlan
                                                            .paymentPlanName || <Dash />}
                                                    </EnterpriseLink>
                                                </Typography>
                                            </PaymentDetailsSidePanelRow>
                                        )}
                                </PaymentDetailsSidePanelGrid>
                            )}
                        </DividerList>
                        {hasLinkedPayment[paymentDetail.transaction.type] && (
                            <PaymentDetailsSidePanelTransactionHistory
                                historyItems={paymentDetail.transactionHistory.items}
                            />
                        )}
                    </Stack>
                ) : (
                    <>
                        {isError && !isFetching ? (
                            <Stack alignment="center" vertical fill>
                                <EmptyState
                                    primaryText={<LocalizedText textKey={TextKey.PayoutDetailEmptyStatePrimary} />}
                                    secondaryText={<LocalizedText textKey={TextKey.PayoutDetailEmptyStateSecondary} />}
                                />
                            </Stack>
                        ) : null}
                    </>
                )
            }
        />
    );
};
